
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type Language = {
  code: string;
  label: string;
  flag: string;
};

const languages: Language[] = [
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang =
    languages.find((l) => l.code === i18n.language) ?? languages[0];

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const switchLanguage = async (code: string) => {
    try {
      await i18n.changeLanguage(code);
    } catch (err) {
      console.error('Failed to change language:', err);
    } finally {
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-sm">{currentLang.flag}</span>
        <span className="text-sm text-white tracking-wide">{currentLang.label}</span>
        <i
          className={`ri-arrow-down-s-line text-white/60 text-sm transition-transform duration-300 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 bg-[#242424] border border-white/10 rounded-lg overflow-hidden shadow-2xl min-w-[130px] z-50"
          role="listbox"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => switchLanguage(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-200 cursor-pointer ${
                lang.code === currentLang.code
                  ? 'bg-[#d4af37]/15 text-[#d4af37]'
                  : 'text-white/80 hover:bg-white/5 hover:text-white'
              }`}
            >
              <span>{lang.flag}</span>
              <span className="tracking-wide">
                {lang.code === 'en' ? 'English' : 'Français'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
